'use client'

import { Builder } from '@builder.io/react'
import type { Input } from '@builder.io/sdk'

import { TopNavV6 } from './TopNavV6'
import { defaultTopNavV6Props } from './TopNavV6.defaults'

const linkSubFields: Input[] = [
  {
    name: 'href',
    type: 'text',
    localized: true,
  },
  {
    name: 'newTab',
    friendlyName: 'Open in a new tab',
    type: 'boolean',
  },
]

const thirdLevelMenuItemFields: Input[] = [
  {
    name: 'title',
    type: 'text',
    localized: true,
  },
  {
    name: 'href',
    type: 'text',
    localized: true,
  },
  {
    name: 'badge',
    type: 'text',
    localized: true,
    helperText: 'Optional badge (ex: New!, Featured, etc.)',
  },
  {
    name: 'isShopAll',
    type: 'boolean',
    friendlyName: 'Is Shop All',
  },
]

const miniMenuItemFields: Input[] = [
  {
    name: 'title',
    type: 'text',
    localized: true,
  },
  {
    name: 'href',
    type: 'text',
    localized: true,
  },
  {
    name: 'badge',
    type: 'text',
    localized: true,
    helperText: 'Optional badge (ex: New!, Featured, etc.)',
  },
]

const violatorFields: Input[] = [
  {
    name: 'title',
    type: 'text',
    localized: true,
  },
  {
    name: 'stamp',
    type: 'text',
    localized: true,
    helperText: 'Optional stamp (ex: New!, Featured, Sale, etc.)',
  },
  {
    name: 'href',
    type: 'text',
    localized: true,
  },
  {
    name: 'newTab',
    friendlyName: 'Open in a new tab',
    type: 'boolean',
  },
  {
    name: 'image',
    type: 'file',
    allowedFileTypes: ['jpeg', 'png', 'webp'],
    localized: true,
  },
  {
    name: 'alt',
    type: 'text',
    localized: true,
  },
  {
    name: 'isVisible',
    type: 'boolean',
    localized: true,
  },
]

const thirdLevelMenuColumnFields: Input[] = [
  {
    name: 'title',
    type: 'text',
    localized: true,
  },
  {
    name: 'items',
    type: 'list',
    subFields: thirdLevelMenuItemFields,
  },
]

const subNavItemFields: Input[] = [
  {
    name: 'title',
    type: 'text',
    localized: true,
  },
  {
    name: 'href',
    type: 'text',
    localized: true,
  },
  {
    name: 'navItems',
    type: 'object',
    friendlyName: 'Third Level Menu',
    subFields: [
      {
        name: 'columns',
        type: 'list',
        subFields: thirdLevelMenuColumnFields,
        helperText: 'Add content columns for text navigation items',
      },
      {
        name: 'violators',
        type: 'list',
        friendlyName: 'Violators',
        subFields: violatorFields,
        helperText: 'Promotional tiles displayed in a separate grid below the content columns',
      },
      {
        name: 'violatorGridSize',
        type: 'number',
        friendlyName: 'Violator Grid Size',
        enum: [
          { label: '0 Columns (No Violators)', value: 0 },
          { label: '1 Column', value: 1 },
          { label: '2 Columns', value: 2 },
          { label: '3 Columns', value: 3 },
          { label: '4 Columns', value: 4 },
        ],
        defaultValue: 1,
        helperText:
          'Number of columns for the violator grid (0-4). If 0 or violators array is empty, content grid will use 4 columns.',
      },
    ],
    helperText:
      'Use this for multi-column menus with sections. Content columns and violators are displayed in separate grids.',
  },
  {
    name: 'miniMenu',
    type: 'object',
    friendlyName: 'Mini Menu',
    subFields: [
      {
        name: 'items',
        type: 'list',
        subFields: miniMenuItemFields,
      },
    ],
    helperText: 'Use this for simple list menus (alternative to Third Level Menu)',
  },
  {
    name: 'ctaButton',
    type: 'object',
    friendlyName: 'CTA Button',
    subFields: [
      {
        name: 'title',
        type: 'text',
        localized: true,
      },
      {
        name: 'href',
        type: 'text',
        localized: true,
      },
    ],
    helperText:
      'CTA button displayed below the content and violator grids for both desktop and mobile',
  },
]

const navItemFields: Input[] = [
  {
    name: 'title',
    type: 'text',
    localized: true,
  },
  {
    name: 'href',
    type: 'text',
    localized: true,
  },
  {
    name: 'authenticatedHref',
    type: 'text',
    friendlyName: 'Authenticated Href',
    localized: true,
    helperText: 'URL to use when user is logged in. If empty, the regular href will be used.',
  },
  {
    name: 'isActive',
    type: 'boolean',
    friendlyName: 'Is Active',
  },
  {
    name: 'navItems',
    type: 'list',
    friendlyName: 'Sub Navigation Items (Desktop & Mobile)',
    subFields: subNavItemFields,
    helperText: 'Sub-navigation items displayed in both desktop and mobile menus',
  },
]

const mobileSecondaryMenuItemFields: Input[] = [
  {
    name: 'title',
    type: 'text',
    localized: true,
  },
  {
    name: 'href',
    type: 'text',
    localized: true,
  },
  {
    name: 'visibility',
    type: 'string',
    friendlyName: 'Visibility',
    enum: ['always', 'loggedIn', 'loggedOut'],
    defaultValue: 'always',
    helperText:
      'Control when this item is shown: "always" (default), "loggedIn" (only for logged in users), or "loggedOut" (only for logged out users)',
  },
]

Builder.registerComponent(TopNavV6, {
  name: 'TopNavV6',
  friendlyName: 'TopNav V6',
  inputs: [
    {
      name: 'logo',
      type: 'object',
      subFields: [
        {
          name: 'src',
          type: 'file',
          friendlyName: 'Logo image',
        },
        {
          name: 'href',
          type: 'string',
          friendlyName: 'Logo link',
          defaultValue: defaultTopNavV6Props.logo?.href,
        },
        {
          name: 'altText',
          type: 'string',
          friendlyName: 'Logo image alt text',
          defaultValue: defaultTopNavV6Props.logo?.altText,
        },
        {
          name: 'width',
          type: 'number',
          defaultValue: defaultTopNavV6Props.logo?.width,
        },
        {
          name: 'height',
          type: 'number',
          defaultValue: defaultTopNavV6Props.logo?.height,
        },
      ],
      defaultValue: defaultTopNavV6Props.logo,
    },
    {
      name: 'navItems',
      friendlyName: 'Navigation Items (Desktop & Mobile)',
      type: 'list',
      subFields: navItemFields,
      defaultValue: defaultTopNavV6Props.navItems,
      helperText: 'Main navigation items displayed in both desktop and mobile menus',
    },
    {
      name: 'mobileHomeButtonText',
      type: 'text',
      localized: true,
      friendlyName: 'Mobile Home Button Text',
      defaultValue: defaultTopNavV6Props.mobileHomeButtonText,
      helperText: 'Text displayed on the Home button in the mobile navigation bar',
    },
    {
      name: 'mobileSecondaryMenuItems',
      friendlyName: 'Mobile Secondary Menu Items',
      type: 'list',
      subFields: mobileSecondaryMenuItemFields,
      defaultValue: defaultTopNavV6Props.mobileSecondaryMenuItems,
      helperText:
        'Additional menu items displayed at the bottom of the mobile menu (e.g., Home, About Us, Support)',
    },
    {
      name: 'cartHref',
      type: 'string',
      friendlyName: 'Cart Link',
      defaultValue: defaultTopNavV6Props.cartHref,
    },
    {
      name: 'joinBodiHref',
      type: 'string',
      friendlyName: 'Join BODi Link',
      defaultValue: defaultTopNavV6Props.joinBodiHref,
    },
    {
      name: 'loginLabel',
      type: 'text',
      localized: true,
      friendlyName: 'Login Button Label',
      defaultValue: defaultTopNavV6Props.loginLabel,
    },
    {
      name: 'joinBodiLabel',
      type: 'text',
      localized: true,
      friendlyName: 'Join BODi Button Label',
      defaultValue: defaultTopNavV6Props.joinBodiLabel,
    },
    {
      name: 'instagramUrl',
      type: 'string',
      friendlyName: 'Instagram URL',
      defaultValue: defaultTopNavV6Props.instagramUrl,
    },
    {
      name: 'facebookUrl',
      type: 'string',
      friendlyName: 'Facebook URL',
      defaultValue: defaultTopNavV6Props.facebookUrl,
    },
    {
      name: 'youtubeUrl',
      type: 'string',
      friendlyName: 'YouTube URL',
      defaultValue: defaultTopNavV6Props.youtubeUrl,
    },
    {
      name: 'account',
      type: 'object',
      subFields: [
        {
          name: 'title',
          type: 'text',
          localized: true,
          friendlyName: 'Logged in title',
          defaultValue: defaultTopNavV6Props.account?.title,
        },
        {
          name: 'loggedOutTitle',
          type: 'text',
          localized: true,
          defaultValue: defaultTopNavV6Props.account?.loggedOutTitle,
        },
        {
          name: 'loggedOutSubtitle',
          type: 'text',
          localized: true,
          defaultValue: defaultTopNavV6Props.account?.loggedOutSubtitle,
        },
        {
          name: 'welcomeCopy',
          type: 'text',
          localized: true,
          defaultValue: defaultTopNavV6Props.account?.welcomeCopy,
          helperText:
            'Displayed under "Account" for logged in users. If the user has a name set, it will be displayed as: "Welcome back, first_name last_name.". If no name is set or if name is "guest" it will displayed: "Welcome back!".',
        },
        {
          name: 'signOutCopy',
          type: 'text',
          localized: true,
          defaultValue: defaultTopNavV6Props.account?.signOutCopy,
          helperText: 'Copy for the sign out button.',
        },
        {
          name: 'logInCopy',
          type: 'text',
          localized: true,
          defaultValue: defaultTopNavV6Props.account?.logInCopy,
        },
        {
          name: 'links',
          type: 'list',
          subFields: [
            {
              name: 'title',
              type: 'text',
              localized: true,
            },
            ...linkSubFields,
          ],
          defaultValue: defaultTopNavV6Props.account?.links,
          helperText: 'List of links displayed when users are logged in or logged out.',
        },
        {
          name: 'bodiCta',
          type: 'object',
          subFields: [
            {
              name: 'title',
              type: 'text',
              localized: true,
            },
            ...linkSubFields,
          ],
          defaultValue: defaultTopNavV6Props.account?.bodiCta,
          helperText:
            'Displayed as a button next to the account icon. If the user is logged in and already has BODi, this will be hidden.',
        },
        {
          name: 'loggedInLinks',
          type: 'list',
          subFields: [
            {
              name: 'title',
              type: 'text',
              localized: true,
            },
            ...linkSubFields,
            {
              name: 'imageUrl',
              type: 'file',
              allowedFileTypes: ['jpeg', 'png', 'webp'],
            },
            {
              name: 'imageAlt',
              type: 'text',
              localized: true,
            },
            {
              name: 'subtitle',
              type: 'text',
              localized: true,
            },
          ],
          defaultValue: defaultTopNavV6Props.account?.loggedInLinks,
        },
      ],
      defaultValue: defaultTopNavV6Props.account,
    },
  ],
  image: 'https://unpkg.com/@tabler/icons@2.44.0/icons/layout-navbar.svg',
})
